import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  Fragment,
} from "react";
import Card from "react-bootstrap/Card";
import Spinner from "react-bootstrap/Spinner";
import Form from "react-bootstrap/Form";
import SimpleTable from "../../../components/atoms/table/simpleTable/simpleTable";
import Button from "../../../components/atoms/button/Button";
import "../../../styles/typography.css";
import { FaChevronDown } from "react-icons/fa";
import {
  getReportsListData,
  exportReports,
  reNotifyReports,
} from "./reportsApi";
import { downloadReports } from "./reportsApi";
import SecondTabNavigation from "../../../components/organisms/SecondTabNavigation";
import DateRangePicker from "../../../components/atoms/daterangePicker/DateRangePicker";
import { toast, ToastContainer } from "react-toastify";
// import { LabAdminTabConfig } from "../LabAdmin.constants";
import { formatDate} from "../../../utility/Formatdate";
import SecondTab from "../../../components/organisms/secondTab";
import PageDescriptionBar from "../../../components/atoms/pageDescriptionBar/PageDescriptionBar";

const Reports = ({ showReNotifyButton = true, TabConfig }) => {

   const today = new Date();
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(today.getMonth() - 1);
  // Filters
  const [fromDate, setFromDate] = useState(oneMonthAgo);
  const [toDate, setToDate] = useState(today);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const fetchIdRef = useRef(0);
  const [loading, setLoading] = useState(false);

  // Table + Selection
  const [selectedRowIds, setSelectedRowIds] = useState(new Set());
  const [selectedSampleIds, setSelectedSampleIds] = useState(new Set());

  const [expandedReportIds, setExpandedReportIds] = useState([]);
  const [expandAll, setExpandAll] = useState(false);

  
  // Pagination
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [reportsData, setReportsData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
   const [paginationData, setPaginationData] = useState({});


  // --- Main Component ---
const tabConfig = [
  { label: "Dashboard", path: "/admin-dashboard" },
  { label: "Scan Sample", path: "/scan-sample" },
  { label: "Scheduler", path: "/scheduler" },
  { label: "Tasks", path: "/tasks" },
  { label: "Requested List", path: "/requested" },
  { label: "Reports", path: "/reports" },
];

const formatSampleData = (reports) =>
  (reports || []).map((sample) => ({
    ...sample,
    requestDate: formatDate(sample.requestDate),
    completeDate: formatDate(sample.completeDate),
    result: sample.result ?? "-",
  }));

   // Expand/collapse all handler
  const handleExpand = () => {
    if (!expandAll) {
      setExpandedReportIds(reportsData.map((r) => r.groupId));
      setExpandAll(true);
    } else {
      setExpandedReportIds([]);
      setExpandAll((prev) => !prev);
    }
  };

   // Individual row expand/collapse
  const handleRowExpandToggle = (reportId) => {
    setExpandedReportIds((prev) =>
      prev.includes(reportId)
        ? prev.filter((id) => id !== reportId)
        : [...prev, reportId]
    );
  };

// --- Sample Table Columns ---
const getSampleColumns = (selectedSampleIds, setSelectedSampleIds) => [
  {
    header: "",
    accessor: "checkbox",
    disableSort: true,
    cell: (sample) => (
      <Form.Check
        type="checkbox"
        checked={selectedSampleIds.has(sample.sampleId)}
        onChange={() =>
          setSelectedSampleIds((prev) => {
            const copy = new Set(prev);
            copy.has(sample.sampleId)
              ? copy.delete(sample.sampleId)
              : copy.add(sample.sampleId);
            return copy;
          })
        }
        aria-label={`Select sample ${sample.sampleId}`}
      />
    ),
  },
  { header: "Sample ID", accessor: "sampleId" },
  { header: "Customer Sample ID", accessor: "customerSampleId" },
  { header: "Request Date", accessor: "requestDate" },
  { header: "Complete Date", accessor: "completeDate" },
  { header: "Species", accessor: "species" },
  { header: "Product", accessor: "product" },
  { header: "Lot No.", accessor: "lotNumber" },
  { header: "Exp Level", accessor: "expectedLevel" },
  { header: "Range", accessor: "range" },
  {
    header: "Result",
    accessor: "result",
    cell: (row) => row.result || "-",
  },
];
// --- Expanded Row ---
const ExpandedRow = ({
  row,
  sampleColumns,
  selectedSampleIds,
  setSelectedSampleIds,
}) => {
  const reportsData = formatSampleData(row.reports);

  return (
    <tr className="expanded-row" key={`${row.groupId}-expanded`}>
      <td
        style={{ width: 48, textAlign: "center", background: "#f5f8fb" }}
      ></td>
      <td colSpan={5} style={{ padding: 0, background: "#f5f8fb" }}>
        <div style={{ margin: "8px 16px" }}>
          <SimpleTable
            columns={sampleColumns}
            data={reportsData}
            customRowRender={(sample, idx) => (
              <tr key={sample.sampleId + idx}>
                <td style={{ textAlign: "center" }}>
                  <Form.Check
                    type="checkbox"
                    checked={selectedSampleIds.has(sample.sampleId)}
                    onChange={() => {
                      setSelectedSampleIds((prev) => {
                        const copy = new Set(prev);
                        copy.has(sample.sampleId)
                          ? copy.delete(sample.sampleId)
                          : copy.add(sample.sampleId);
                        return copy;
                      });
                    }}
                    aria-label={`Select sample ${sample.sampleId}`}
                  />
                </td>
                <td>{sample.sampleId}</td>
                <td>{sample.customerSampleId}</td>
                <td>{sample.requestDate}</td>
                <td>{sample.completeDate}</td>
                <td>{sample.species}</td>
                <td>{sample.product}</td>
                <td>{sample.lotNumber}</td>
                <td>{sample.expectedLevel}</td>
                <td>{sample.range}</td>
                <td>{sample.result || "-"}</td>
              </tr>
            )}
            showPagination={reportsData.length > 5}
          />
        </div>
      </td>
    </tr>
  );
};


  const fetchData = async (search, currentPage = 1, rowsPerPage = 25) => {
    const fetchId = ++fetchIdRef.current;
    setLoading(true);
    try {
      const res = await getReportsListData({
        FromDate: fromDate ? formatDate(new Date(fromDate)) : "",
        ToDate: toDate ? formatDate(new Date(toDate)) : "",
        PageNumber: currentPage,
        PageSize: rowsPerPage,
        ...(debouncedSearchTerm && { FilterText: debouncedSearchTerm }),
      });
      if (fetchId !== fetchIdRef.current) return;
      const { totalRecords, pageNumber, pageSize, totalPages } = res;
      setPaginationData({ totalRecords, pageNumber, pageSize, totalPages });
      setReportsData(Array.isArray(res.results) ? res.results : []);
      setTotalRecords(res.totalRecords);
    } catch {
      if (fetchId !== fetchIdRef.current) return;
      setReportsData([]);
    } finally {
      if (fetchId === fetchIdRef.current) setLoading(false);
    }
  };

  // Debounce search term — avoids firing an API call on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchData();
  }, [fromDate, toDate, currentPage, rowsPerPage, debouncedSearchTerm]);

  // Filter + Search — uses debouncedSearchTerm to stay in sync with the API call
  const filteredData = useMemo(() => {
    return reportsData
      .map((item) => {
        // Check if group matches
        const groupMatches = debouncedSearchTerm
          ? [
              item.companyName,
              item.locationName,
              String(item.groupId),
              item.userName,
            ].some(
              (f) => f && f.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
            )
          : true;

        // Filter reports inside each group
        const filteredReports = item.reports.filter((report) =>
          debouncedSearchTerm
            ? Object.values(report).some(
                (val) =>
                  val &&
                  val
                    .toString()
                    .toLowerCase()
                    .includes(debouncedSearchTerm.toLowerCase())
              )
            : true
        );

        // If group matches, show all reports; else show only matching reports
        if (groupMatches) {
          return { ...item, reports: item.reports };
        } else if (filteredReports.length > 0) {
          return { ...item, reports: filteredReports };
        }
        return null;
      })
      .filter((item) => item && item.reports.length > 0);
  }, [reportsData, debouncedSearchTerm]);

  // Show all filtered data, no pagination
  const paginatedData = filteredData;

  // Select/Deselect rows
  const isAllSelectedOnPage =
    paginatedData.length > 0 &&
    paginatedData.every((r) => selectedRowIds.has(r.groupId));

  const isGroupFullySelected = useCallback((groupId) => {
    const group = reportsData.find(r => r.groupId === groupId);
    if (!group?.reports || group.reports.length === 0) return false;
    return group.reports.every(sample => selectedSampleIds.has(sample.sampleId));
  }, [reportsData, selectedSampleIds]);

    useEffect(() => {
    setSelectedRowIds((prevSelectedRows) => {
      const newSelectedRows = new Set(prevSelectedRows);
      reportsData.forEach((group) => {
        const allSamplesSelected = isGroupFullySelected(group.groupId);
        if (allSamplesSelected && group.reports?.length > 0) {
          newSelectedRows.add(group.groupId);
        } else {
          newSelectedRows.delete(group.groupId);
        }
      });
      return newSelectedRows;
    });
  }, [selectedSampleIds, reportsData, isGroupFullySelected]);
  
  const toggleRowSelection = useCallback(
    (groupId) => {
      const mainRow = reportsData.find((r) => r.groupId === groupId);
      const isCurrentlySelected = selectedRowIds.has(groupId);
      
      // Update row selection
      setSelectedRowIds((prev) => {
        const copy = new Set(prev);
        if (isCurrentlySelected) {
          copy.delete(groupId);
        } else {
          copy.add(groupId);
        }
        return copy;
      });
      
      // Update sample selection accordingly
      if (mainRow?.reports) {
        setSelectedSampleIds((prevSamples) => {
          const samplesCopy = new Set(prevSamples);
          
          if (isCurrentlySelected) {
            // Unchecking row - remove all samples from this group
            mainRow.reports.forEach((sample) => {
              samplesCopy.delete(sample.sampleId);
            });
          } else {
            // Checking row - add all samples from this group
            mainRow.reports.forEach((sample) => {
              samplesCopy.add(sample.sampleId);
            });
          }
          
          return samplesCopy;
        });
      }
    },
    [reportsData, selectedRowIds]
  );

  const toggleSelectAllOnPage = () => {
    const updateSelection = (setter, getId) => {
      setter((prev) => {
        const copy = new Set(prev);
        if (!isAllSelectedOnPage) {
          paginatedData.forEach((r) =>
            r.reports?.forEach((s) => copy.add(getId(r, s)))
          );
        } else {
          paginatedData.forEach((r) =>
            r.reports?.forEach((s) => copy.delete(getId(r, s)))
          );
        }
        return copy;
      });
    };

    updateSelection(setSelectedRowIds, (r) => r.groupId);
    updateSelection(setSelectedSampleIds, (_, s) => s.sampleId);
  };

  // Table Columns
  const columns = useMemo(
    () => [
      {
        header: (
          <Form.Check
            type="checkbox"
            checked={isAllSelectedOnPage}
            onChange={toggleSelectAllOnPage}
            aria-label="Select all"
          />
        ),
        accessor: "checkbox",
        disableSort: true,
        cell: (row) => (
          <Form.Check
            type="checkbox"
            checked={selectedRowIds.has(row.groupId)}
            onChange={() => toggleRowSelection(row.groupId)}
            aria-label={`Select ${row.groupId}`}
          />
        ),
      },
      { header: "Company", accessor: "companyName" },
      { header: "Location", accessor: "locationName" },
      { header: "Group ID", accessor: "groupId" },
      { header: "User Name", accessor: "userName" },
      {
             header: (
           <Button onClick={handleExpand}>
             {expandAll ? "Collapse All" : "Expand All"}
           </Button>
         ),
        accessor: "expand",
        disableSort: true,
        cell: (row) => (
          <div
            onClick={() => {
              handleRowExpandToggle(row.groupId);
            }}
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            aria-label={
              expandedReportIds.includes(row.groupId)
            ? "Collapse details"
            : "Expand details"
            }
          >
            <FaChevronDown
              size={14}
              style={{
                transition: "transform .2s",
                transform:
                  expandedReportIds === row.groupId ? "rotate(180deg)" : "none",
              }}
            />
          </div>
        ),
      },
    ],
    [isAllSelectedOnPage, selectedRowIds, toggleRowSelection, expandedReportIds]
  );

  // Sample table columns
  const sampleColumns = getSampleColumns(
    selectedSampleIds,
    setSelectedSampleIds
  );

  const anySelected = selectedRowIds.size > 0 || selectedSampleIds.size > 0;

  const buildSelectedReportParams = useCallback(() => {
    const sampleIds = new Set();

    reportsData.forEach((group) => {
      if (selectedRowIds.has(group.groupId) && Array.isArray(group.reports)) {
        group.reports.forEach((sample) => {
          if (sample.sampleId) {
            sampleIds.add(sample.sampleId);
          }
        });
      }

      if (Array.isArray(group.reports)) {
        group.reports.forEach((sample) => {
          if (selectedSampleIds.has(sample.sampleId) && sample.sampleId) {
            sampleIds.add(sample.sampleId);
          }
        });
      }
    });

    return {
      groupIds: Array.from(selectedRowIds),
      sampleIds: Array.from(sampleIds),
    };
  }, [reportsData, selectedRowIds, selectedSampleIds]);

  const handleExport = async () => {
    try {
      if (selectedSampleIds.size === 0 && selectedRowIds.size === 0) {
        toast.error("Please select at least one sample or group to Export.");
        return;
      }

      const params = buildSelectedReportParams();

      const response = await exportReports(params);

      // Handle file download (Excel)
      const blob = new Blob([response], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", "report.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      toast.success("Report exported successfully.");
    } catch (error) {
      toast.error("Failed to export report.");
    }
  };

  const handleViewReport = async () => {
    try {
      if (selectedSampleIds.size === 0 && selectedRowIds.size === 0) {
        toast.error("Please select at least one sample or group to view.");
        return;
      }

      const params = buildSelectedReportParams();
      const response = await downloadReports(params);

      const blob = new Blob([response], { type: "application/pdf" });
      const viewUrl = window.URL.createObjectURL(blob);

      window.open(viewUrl, "_blank", "noopener,noreferrer");

      setTimeout(() => {
        window.URL.revokeObjectURL(viewUrl);
      }, 60000);

      toast.success("Report opened successfully.");
    } catch (error) {
      toast.error("Failed to open report.");
    }
  };

  const handleDownload = async () => {
    try {
      if (selectedSampleIds.size === 0 && selectedRowIds.size === 0) {
        toast.error("Please select at least one sample or group to download.");
        return;
      }

      const params = buildSelectedReportParams();
      const response = await downloadReports(params);

      // Download file (assume PDF, update MIME type if Excel)
      const blob = new Blob([response], { type: "application/pdf" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", "lab-analysis-report.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      toast.success("Report downloaded successfully.");
    } catch (error) {
      toast.error("Failed to download report.");
    }
  };

  const handleReNotify = async () => {
    try {
      // Collect selected sampleIds
      const sampleIds = [];
      reportsData.forEach((group) => {
        if (selectedRowIds.has(group.groupId) && Array.isArray(group.reports)) {
          group.reports.forEach((sample) => {
            if (sample.sampleId) sampleIds.push(sample.sampleId);
          });
        }
        if (Array.isArray(group.reports)) {
          group.reports.forEach((sample) => {
            if (
              selectedSampleIds.has(sample.sampleId) &&
              sample.sampleId
            ) {
              sampleIds.push(sample.sampleId);
            }
          });
        }
      });
      if (sampleIds.length === 0) {
        toast.error("Please select at least one sample to re-notify.");
        return;
      }
      const response = await reNotifyReports(sampleIds);
      if (response === "success") {
        toast.success("Re-notification sent successfully.");
      } else {
        toast.error("Failed to send re-notification.");
      }
    } catch (error) {
      toast.error("Failed to send re-notification.");
    }
  };

  return (
    <Fragment>
      <SecondTab tabConfig={TabConfig} />
      <PageDescriptionBar items={[
        { type: "icon", icon: "📅", text: "Date Range – Filter reports by submitted date" },
        { type: "icon", icon: "🏢", text: "Company – Narrow results to a specific company" },
        { type: "icon", icon: "📍", text: "Location – Filter by lab or customer location" },
        { type: "separator" },
        { type: "icon", icon: "📤", text: "Export – Download filtered report data as a spreadsheet" },
        { type: "icon", icon: "🔔", text: "Re-Notify – Resend the report notification to the customer", note: "Lab Admin only" },
      ]} />
      <div className="p-3 typography">
        {/* --- Filters Row --- */}
        <div
          className="d-flex flex-wrap gap-3 shadow-lg mb-2 align-items-center p-3"
          style={{ backgroundColor: "#fff", borderRadius: "5px" }}
        >
          {/* Custom Date Picker */}
          <DateRangePicker
            fromDate={fromDate}
            toDate={toDate}
            onFromDateChange={setFromDate}
            onToDateChange={setToDate}
            fromLabel="From"
            toLabel="To"
            className="me-3"
          />

          {/* Search */}
          <div className="d-flex align-items-center gap-1">
            <Button
              variant="outlined"
              onClick={() => {
                setCurrentPage(1);
                fetchData();
              }}
            >
              Search
            </Button>
          </div>

          {/* Right-aligned Action Buttons */}
          <div className="ms-auto d-flex gap-2">
            {showReNotifyButton && (
              <Button
                key="Re-Notify"
                size="md"
                variant={anySelected ? "active" : "disabled"}
                disabled={!anySelected}
                onClick={handleReNotify}
              >
                Re-Notify
              </Button>
            )}
            <Button
              key="Export"
              size="md"
              variant={anySelected ? "active" : "disabled"}
              disabled={!anySelected}
              onClick={handleExport}
            >
              Export
            </Button>
            <Button
              key="ViewReport"
              size="md"
              variant={anySelected ? "active" : "disabled"}
              disabled={!anySelected}
              onClick={handleViewReport}
            >
              View Report
            </Button>
            <Button
              key="Download"
              size="md"
              variant={anySelected ? "active" : "disabled"}
              disabled={!anySelected}
              onClick={handleDownload}
            >
              Download
            </Button>
            <div
              className="line-separator"
              style={{
                width: "1px",
                height: "46px",
                backgroundColor: "rgba(0, 0, 0, 0.3)",
              }}
            />
            <Form.Control
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              style={{ width: 180, height: 44, borderRadius: 5 }}
              size="sm"
            />
          </div>
        </div>
        <Card>
          <Card.Body className="p-0">
            {/* --- Table --- */}
            {loading ? (
              <div style={{ textAlign: "center", padding: "60px 0" }}>
                <Spinner animation="border" /> Loading...
              </div>
            ) : (
              <SimpleTable
                title=""
                columns={columns}
                data={paginatedData}
                customRowRender={(row) => (
                  <React.Fragment key={row.groupId}>
                    <tr>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedRowIds.has(row.groupId)}
                          onChange={() => toggleRowSelection(row.groupId)}
                          aria-label={`Select ${row.groupId}`}
                        />
                      </td>
                      <td>{row.companyName}</td>
                      <td>{row.locationName}</td>
                      <td>{row.groupId}</td>
                      <td>{row.userName}</td>
                      <td>
                        <div
                          onClick={() => handleRowExpandToggle(row.groupId)}
                          style={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                          aria-label={
                            expandedReportIds.includes(row.groupId)
                              ? "Collapse details"
                              : "Expand details"
                          }
                        >
                          <FaChevronDown
                            size={14}
                            style={{
                              transition: "transform .2s",
                              transform:
                                expandedReportIds.includes(row.groupId)
                                  ? "rotate(180deg)"
                                  : "none",
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                    {expandedReportIds.includes(row.groupId) && (
                      <ExpandedRow
                        row={row}
                        sampleColumns={sampleColumns}
                        selectedSampleIds={selectedSampleIds}
                        setSelectedSampleIds={setSelectedSampleIds}
                      />
                    )}
                  </React.Fragment>
                )}
                showPagination
                paginationData={paginationData}
                searchValue={searchTerm}
                fetchData={fetchData}
              />
            )}
          </Card.Body>
        </Card>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </Fragment>
  );
};

export default Reports;
